import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Route wrapper that redirects unauthenticated users to `/login`.
 *
 * Avoids the "Sign in page flash on refresh" race: `useIsAuthenticated()`
 * is React state that propagates async via the MsalProvider event bus, so
 * on the first render after a hard refresh it can briefly return `false`
 * even when MSAL already has a cached account from sessionStorage.
 *
 * We add a synchronous fallback: if MSAL isn't `None`-status (i.e. it's
 * still booting or processing a redirect), or if `accounts` already
 * contains the cached account, we treat the user as authenticated and
 * skip the redirect. The pre-redirect path is stashed in router state so
 * {@link Login} can bounce the user back where they were after a
 * successful sign-in.
 */
export function RequireAuth({ children }: RequireAuthProps): JSX.Element {
  const isAuthenticated = useIsAuthenticated();
  const { accounts, inProgress } = useMsal();
  const location = useLocation();

  // Treat any of these as "still booting" — render nothing rather than
  // redirect prematurely:
  //  - inProgress != None: MSAL is processing a redirect / acquire / login
  //  - accounts is non-empty but isAuthenticated hasn't synced yet
  const stillBooting = inProgress !== InteractionStatus.None;
  const hasCachedAccount = accounts.length > 0;

  if (isAuthenticated || hasCachedAccount) {
    return <>{children}</>;
  }
  if (stillBooting) {
    // Render a transparent placeholder so the layout doesn't shift.
    return <div className="min-h-[200px]" aria-hidden="true" />;
  }
  return <Navigate to="/login" state={{ from: location.pathname }} replace />;
}
